import React, { createContext, FunctionComponent, ReactNode, useContext, useMemo, useState } from 'react'

import { clearStoredDevMocks, readActiveDevMocks, writeDevMocks } from './devMocksStorage'
import { DevMocks, NO_DEV_MOCKS } from './devMocksTypes'

interface DevMocksContextValue {
  devMocks: DevMocks
  setDevMocks: (mocks: DevMocks) => void
  resetDevMocks: () => void
}

const noop = () => {}

export const DevMocksContext = createContext<DevMocksContextValue>({
  devMocks: NO_DEV_MOCKS,
  setDevMocks: noop,
  resetDevMocks: noop,
})

/**
 * [ENGBEND-12942] Holds the stand-in data the dev mocks panel sets, so a feature can be exercised
 * before its endpoints exist. Reads nothing in production, which is the single place that
 * guarantee lives: consumers can read the mocks unguarded.
 */
export const DevMocksProvider: FunctionComponent<{ children: ReactNode }> = ({ children }) => {
  const [devMocks, setStoredDevMocks] = useState<DevMocks>(readActiveDevMocks)

  const value = useMemo<DevMocksContextValue>(
    () => ({
      devMocks,
      setDevMocks: (mocks: DevMocks) => {
        writeDevMocks(mocks)
        setStoredDevMocks(mocks)
      },
      resetDevMocks: () => {
        clearStoredDevMocks()
        setStoredDevMocks(NO_DEV_MOCKS)
      },
    }),
    [devMocks],
  )

  return <DevMocksContext.Provider value={value}>{children}</DevMocksContext.Provider>
}

export const useDevMocks = (): DevMocksContextValue => useContext(DevMocksContext)
